import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { DateTime } from 'luxon';
import 'react-datepicker/dist/react-datepicker.css';
import './statCharts.css';

/**
 * One date-range row above a statistics view: presets first (the ones people
 * actually reach for), a custom from/to, and — where the view buckets time —
 * a Daily / Weekly switch. NOTHING LOADS UNTIL APPLY (owner, 2026-09-09: "I
 * need the apply button before reloading"): clicks build a draft; Apply
 * commits it, and only then do the charts refetch. Dates are Dubai calendar
 * days as YYYY-MM-DD strings; every chart, tile and table below re-renders
 * against the same slice, so the figures always agree with each other.
 */
// Dubai calendar days via luxon (the house rule): today is today in Dubai
// whatever the viewer's clock says, and shifting a day never touches a clock.
export const dubaiToday = () => DateTime.now().setZone('Asia/Dubai').toISODate();
export const shiftDays = (iso, n) => DateTime.fromISO(iso, { zone: 'Asia/Dubai' }).plus({ days: n }).toISODate();
const monthStart = (iso) => DateTime.fromISO(iso, { zone: 'Asia/Dubai' }).startOf('month').toISODate();

export const PRESETS = [
    { key: 'today', label: 'Today', range: () => { const t = dubaiToday(); return { from: t, to: t }; } },
    { key: '7d', label: 'Last 7 days', range: () => { const t = dubaiToday(); return { from: shiftDays(t, -6), to: t }; } },
    { key: '30d', label: 'Last 30 days', range: () => { const t = dubaiToday(); return { from: shiftDays(t, -29), to: t }; } },
    { key: '90d', label: 'Last 90 days', range: () => { const t = dubaiToday(); return { from: shiftDays(t, -89), to: t }; } },
    { key: 'mtd', label: 'This month', range: () => { const t = dubaiToday(); return { from: monthStart(t), to: t }; } },
    { key: 'lastm', label: 'Last month', range: () => {
        const t = dubaiToday(); const end = shiftDays(monthStart(t), -1); return { from: monthStart(end), to: end }; } },
];

const isDay = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ''));

/**
 * The APPLIED range lives in the URL (?from=&to=&granularity=, or ?all=1) so
 * a view can be bookmarked or sent to a colleague at exactly the slice being
 * looked at. Memoised on the primitives: the views fetch in an effect keyed
 * on this value, and a fresh object per render re-fired that effect forever.
 */
export function useRangeParams(defaults) {
    const [params, setParams] = useSearchParams();
    const pAll = params.get('all'); const pFrom = params.get('from'); const pTo = params.get('to'); const pGran = params.get('granularity');
    const dFrom = defaults.from; const dTo = defaults.to; const dGran = defaults.granularity;
    const value = useMemo(() => ({
        from: dFrom, to: dTo, ...(dGran ? { granularity: dGran } : {}),
        ...(pAll === '1' ? { from: '', to: '' } : {}),
        ...(isDay(pFrom) ? { from: pFrom } : {}),
        ...(isDay(pTo) ? { to: pTo } : {}),
        ...(dGran && ['day', 'week'].includes(pGran) ? { granularity: pGran } : {}),
    }), [pAll, pFrom, pTo, pGran, dFrom, dTo, dGran]);
    const setValue = useCallback((next) => {
        setParams((prev) => {
            const p = new URLSearchParams(prev);
            if (next.from) p.set('from', next.from); else p.delete('from');
            if (next.to) p.set('to', next.to); else p.delete('to');
            if (next.granularity) p.set('granularity', next.granularity); else p.delete('granularity');
            if (!next.from && !next.to) p.set('all', '1'); else p.delete('all');
            return p;
        });
    }, [setParams]);
    return [value, setValue];
}

// The picker speaks in JS Date objects of the browser's zone; the range
// speaks in day strings. Luxon does both conversions so no day can drift.
const toDate = (iso) => (iso ? DateTime.fromISO(iso).toJSDate() : null);
const fromDate = (d) => (d ? DateTime.fromJSDate(d).toISODate() : null);
const same = (a, b) => a.from === b.from && a.to === b.to && (a.granularity || '') === (b.granularity || '');

export default function StatDateRange({ value, onChange, withGranularity = false, allTime = false, loading = false }) {
    const [draft, setDraft] = useState(value);
    useEffect(() => { setDraft(value); }, [value]);
    const dirty = !same(draft, value);
    const isAllTime = !draft.from && !draft.to;
    const activePreset = isAllTime ? (allTime ? 'all' : null)
        : (PRESETS.find((p) => { const r = p.range(); return r.from === draft.from && r.to === draft.to; })?.key || null);
    const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

    return (
        <div className="sc-filters" role="group" aria-label="Date range">
            <div className="sc-presets">
                {allTime && (
                    <button type="button" className={`sc-preset${activePreset === 'all' ? ' is-active' : ''}`} onClick={() => set({ from: '', to: '' })}>
                        {activePreset === 'all' && <span className="sc-presetCheck" aria-hidden="true">✓</span>}
                        All time
                    </button>
                )}
                {PRESETS.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        className={`sc-preset${activePreset === p.key ? ' is-active' : ''}`}
                        onClick={() => set(p.range())}
                    >
                        {activePreset === p.key && <span className="sc-presetCheck" aria-hidden="true">✓</span>}
                        {p.label}
                    </button>
                ))}
            </div>
            <div className="sc-custom">
                <span className="sc-customLabel">From</span>
                <DatePicker
                    selected={toDate(draft.from)}
                    onChange={(d) => { const iso = fromDate(d); if (iso) set({ from: iso, to: draft.to && draft.to < iso ? iso : draft.to }); }}
                    dateFormat="dd/MM/yyyy"
                    maxDate={toDate(draft.to || dubaiToday())}
                    placeholderText="start"
                    className="sc-dateInput"
                />
                <span className="sc-customLabel">to</span>
                <DatePicker
                    selected={toDate(draft.to)}
                    onChange={(d) => { const iso = fromDate(d); if (iso) set({ to: iso, from: draft.from && draft.from > iso ? iso : draft.from }); }}
                    dateFormat="dd/MM/yyyy"
                    minDate={toDate(draft.from || null)}
                    maxDate={toDate(dubaiToday())}
                    placeholderText="end"
                    className="sc-dateInput"
                />
            </div>
            {withGranularity && (
                <div className="sc-gran" role="group" aria-label="Bucket by">
                    {['day', 'week'].map((g) => (
                        <button
                            key={g}
                            type="button"
                            className={`sc-granBtn${draft.granularity === g ? ' is-active' : ''}`}
                            onClick={() => set({ granularity: g })}
                        >
                            {g === 'day' ? 'Daily' : 'Weekly'}
                        </button>
                    ))}
                </div>
            )}
            <button
                type="button"
                className={`sc-apply${dirty ? ' is-dirty' : ''}`}
                disabled={!dirty || loading}
                onClick={() => onChange(draft)}
                title={dirty ? 'Load the figures for this range' : 'These figures are for the range shown'}
            >
                {loading ? 'Loading…' : 'Apply'}
            </button>
        </div>
    );
}
